import { Component, Input, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { SupportApiService } from '../core/support-api.service';

/** Spec — assignment control. The picker only ever offers principals the parent
 * supplies via `assignableAgents` (queue-grant-scoped), never an org-wide or
 * free-text picker.
 *
 * That fast-follow has now landed (AB#1883): `GET /queues/{id}/grantees` resolves
 * "who holds a grant on this ticket's queue", and ticket-detail fills this input from
 * it. The route applies both filters the assignment WRITE applies — a live
 * respond/assign grant on an active principal, plus a current certification for a VAR
 * — so every option offered here is one `PATCH /tickets/{id}` would accept. This
 * component stays deliberately dumb: it renders what it is given and never fetches,
 * so the scoping decision has exactly one home.
 *
 * The empty state below is still reachable and still correct — a queue may genuinely
 * have no eligible grantee, and a failed roster read degrades to it rather than
 * breaking the page. */
@Component({
  selector: 'app-assign-control',
  standalone: true,
  imports: [FormsModule, NgbTooltip],
  template: `
    <div class="bt-field">
      <label class="bt-label" ngbTooltip="Only principals holding a grant on this ticket's queue are offered">Assign to</label>
      <select class="bt-select" data-testid="assign-select" [(ngModel)]="selected" (change)="assign()">
        <option [ngValue]="undefined">Unassigned</option>
        @for (agent of assignableAgents; track agent.id) {
          <option [value]="agent.id">{{ agent.email }}</option>
        }
      </select>
      @if (!assignableAgents.length) {
        <span class="assign-hint">No grantees resolved for this queue yet.</span>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
    .assign-hint { font-size: 12px; color: var(--bt-text-muted); }
  `],
})
export class AssignControlComponent {
  private api = inject(SupportApiService);

  @Input() ticketId = '';
  @Input() assignableAgents: { id: string; email: string }[] = [];
  selected?: string;

  assign(): void {
    if (!this.selected) return;
    this.api.patchTicket(this.ticketId, { assignee_principal_id: this.selected }).subscribe();
  }
}
